import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";

import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { accessApi, accessKeys } from "./api";
import { CODE_LENGTH, isCodeComplete, normalizeCode } from "./codes";
import { CLOSED, elevationReducer } from "./elevation-flow";
import { readFailure } from "./failure";

/**
 * The step-up ([F-033], [AC-058], [AC-059]) as ONE dialog the whole app
 * shares. A screen wraps a critical request in `withElevation(...)`; when the
 * server refuses it for a missing elevation (403, `requires: "elevation"`),
 * the dialog opens, mails a code to the person's own address, takes the code,
 * and then runs the request again - the screen sees one call that succeeded.
 *
 * The elevation belongs to this session and lasts `ELEVATION_WINDOW`; within
 * it the server asks nothing, so the dialog does not appear again. Cancelling
 * rejects the wrapped call with `ElevationCancelled`, which screens ignore -
 * the person chose not to, there is nothing to report.
 */

export class ElevationCancelled extends Error {
  constructor() {
    super("elevation cancelled");
    this.name = "ElevationCancelled";
  }
}

export function isElevationCancelled(error: unknown): boolean {
  return error instanceof ElevationCancelled;
}

interface Waiting {
  retry: () => void;
  cancel: () => void;
}

interface ElevationApi {
  /** Run a request; on a refusal for missing elevation, elevate and run it again. */
  withElevation: <T>(action: () => Promise<T>) => Promise<T>;
  /** Open the dialog without a refused request - the "elevate" button of the account page. */
  elevate: () => Promise<void>;
}

const ElevationContext = createContext<ElevationApi | undefined>(undefined);

export function ElevationProvider({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(elevationReducer, CLOSED);
  const [code, setCode] = useState("");
  const waiting = useRef<Waiting[]>([]);

  const wait = useCallback(<T,>(action: () => Promise<T>) => {
    return new Promise<T>((resolve, reject) => {
      waiting.current.push({
        retry: () => void action().then(resolve, reject),
        cancel: () => reject(new ElevationCancelled()),
      });
      setCode("");
      dispatch({ type: "open" });
    });
  }, []);

  const withElevation = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T> => {
      try {
        return await action();
      } catch (error) {
        if (!readFailure(error, "").needsElevation) throw error;
        return wait(action);
      }
    },
    [wait],
  );

  const elevate = useCallback(() => wait(async () => undefined), [wait]);

  const finish = useCallback(
    (outcome: "retry" | "cancel") => {
      const all = waiting.current;
      waiting.current = [];
      for (const entry of all) entry[outcome]();
    },
    [],
  );

  const cancel = (): void => {
    dispatch({ type: "cancel" });
    finish("cancel");
  };

  const elevated = async (): Promise<void> => {
    dispatch({ type: "elevated" });
    // `elevated_until` lives in ["access"]: the own-account page shows it.
    await queryClient.invalidateQueries({ queryKey: accessKeys.all });
    finish("retry");
  };

  const request = async (): Promise<void> => {
    dispatch({ type: "submit" });
    try {
      const reply = await accessApi.elevateRequest();
      if (reply.already) {
        await elevated();
        return;
      }
      setCode("");
      dispatch({ type: "sent" });
    } catch (error) {
      dispatch({ type: "failed", message: readFailure(error, t("elevation.requestFailed")).message });
    }
  };

  const confirm = async (): Promise<void> => {
    if (!isCodeComplete(code)) return;
    dispatch({ type: "submit" });
    try {
      await accessApi.elevate(normalizeCode(code));
      await elevated();
    } catch (error) {
      dispatch({ type: "failed", message: readFailure(error, t("elevation.confirmFailed")).message });
    }
  };

  const api = useMemo<ElevationApi>(() => ({ withElevation, elevate }), [withElevation, elevate]);

  return (
    <ElevationContext.Provider value={api}>
      {children}
      <ElevationDialog
        step={state.step}
        busy={state.busy}
        error={state.error}
        code={code}
        onCode={setCode}
        onRequest={() => void request()}
        onConfirm={() => void confirm()}
        onCancel={cancel}
      />
    </ElevationContext.Provider>
  );
}

export function useElevation(): ElevationApi {
  const ctx = useContext(ElevationContext);
  if (!ctx) throw new Error("useElevation must be used within <ElevationProvider>");
  return ctx;
}

/** The dialog, driven entirely by props. */
export function ElevationDialog({
  step,
  busy,
  error,
  code,
  onCode,
  onRequest,
  onConfirm,
  onCancel,
}: {
  step: "closed" | "request" | "code";
  busy: boolean;
  error: string | null;
  code: string;
  onCode: (value: string) => void;
  onRequest: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const email = user?.email ?? "";

  return (
    <Dialog
      open={step !== "closed"}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent data-testid="elevation_dialog">
        <DialogHeader>
          <DialogTitle data-testid="elevation_title" className="flex items-center gap-2">
            <ShieldCheck aria-hidden="true" className="h-5 w-5" />
            {t("elevation.title")}
          </DialogTitle>
          <DialogDescription data-testid="elevation_lead">
            {step === "code" ? t("elevation.sent", { email }) : t("elevation.lead", { email })}
          </DialogDescription>
        </DialogHeader>

        {step === "code" ? (
          <form
            data-testid="elevation_form"
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            <Label htmlFor="elevation-code">{t("elevation.code")}</Label>
            <Input
              id="elevation-code"
              data-testid="elevation_code"
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={CODE_LENGTH + 2}
              onChange={(event) => onCode(event.target.value)}
            />
          </form>
        ) : null}

        {error ? (
          <p data-testid="elevation_error" role="alert" className="text-sm text-destructive-text">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2">
          <Button data-testid="elevation_cancel" variant="outline" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          {step === "code" ? (
            <>
              <Button data-testid="elevation_resend" variant="ghost" disabled={busy} onClick={onRequest}>
                {t("elevation.resend")}
              </Button>
              <Button data-testid="elevation_confirm" disabled={busy || !isCodeComplete(code)} onClick={onConfirm}>
                {t("elevation.confirm")}
              </Button>
            </>
          ) : (
            <Button data-testid="elevation_request" disabled={busy} onClick={onRequest}>
              {t("elevation.request")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
